import { api } from './api';
import type {
  Route,
  RouteRequest,
  RouteProfile,
  Coordinates,
  RouteLeg,
  GeoJSON,
} from '@/types';
type ResponseEnvelope = Record<string, unknown>;

const isRecord = (value: unknown): value is ResponseEnvelope => {
  return !!value && typeof value === 'object' && !Array.isArray(value);
};

const unwrapEnvelope = (payload: unknown): unknown => {
  let current = payload;
  const seen = new Set<object>();

  while (isRecord(current) && Object.prototype.hasOwnProperty.call(current, 'data')) {
    const next = current.data;
    if (!isRecord(next) && !Array.isArray(next)) {
      return next;
    }
    if (isRecord(next) && seen.has(next)) {
      break;
    }
    if (isRecord(next)) {
      seen.add(next);
    }
    current = next;
  }

  return current;
};

const extractRoutes = (payload: unknown): Route[] => {
  const unwrapped = unwrapEnvelope(payload);
  if (Array.isArray(unwrapped)) {
    return unwrapped as Route[];
  }
  if (!isRecord(unwrapped)) return [];
  if (Array.isArray(unwrapped.routes)) {
    return unwrapped.routes as Route[];
  }
  return [];
};

const extractWaypointResponse = (payload: unknown): {
  routes: EnhancedRoute[];
  waypoints: Coordinates[];
  optimized: boolean;
  optimizedOrder: number[];
} => {
  const unwrapped = unwrapEnvelope(payload);
  if (!isRecord(unwrapped)) {
    return {routes: [], waypoints: [], optimized: false, optimizedOrder: []};
  }

  const routes = Array.isArray(unwrapped.routes)
    ? (unwrapped.routes as EnhancedRoute[])
    : [];

  const waypoints = Array.isArray(unwrapped.waypoints)
    ? (unwrapped.waypoints as Coordinates[])
    : [];

  // optimized_order is the visiting order expressed as the original waypoint
  // indices (what we sent), e.g. [0,2,3,1,4].
  const optimizedOrder = Array.isArray(unwrapped.optimized_order)
    ? (unwrapped.optimized_order as unknown[]).filter((n): n is number => typeof n === 'number')
    : [];

  return {
    routes,
    waypoints,
    optimized: typeof unwrapped.optimized === 'boolean' ? unwrapped.optimized : false,
    optimizedOrder,
  };
};

export type RouteType = 'fastest' | 'shortest' | 'scenic' | 'eco';

export interface RouteTypeInfo {
  id: RouteType;
  name: string;
  description: string;
  icon: string;
}

export interface RoutePreference {
  id: string;
  name: string;
  type: 'boolean';
}

export interface EnhancedRouteRequest {
  origin: Coordinates;
  destination: Coordinates;
  waypoints?: Coordinates[];
  profile: RouteProfile;
  alternatives?: boolean;
  routeType?: RouteType;
  avoidTolls?: boolean;
  avoidHighways?: boolean;
  avoidFerries?: boolean;
  avoidUnpaved?: boolean;
  departureTime?: string;
  trafficModel?: 'best_guess' | 'pessimistic' | 'optimistic';
}

export interface EnhancedRoute {
  id: string;
  geometry: GeoJSON.LineString;
  distance: number;
  duration: number;
  durationInTraffic?: number;
  legs: RouteLeg[];
  summary: string;
  routeType?: RouteType;
  trafficCondition?: 'light' | 'moderate' | 'heavy' | 'severe';
  warnings?: string[];
}

export interface DistanceMatrixResult {
  origins: Coordinates[];
  destinations: Coordinates[];
  rows: {
    elements: {
      distance: number;
      duration: number;
      status: 'OK' | 'NOT_FOUND' | 'ZERO_RESULTS';
    }[];
  }[];
}

export interface IsochroneContour {
  time: number; // minutes (the time budget for this reachability ring)
  distance?: number; // mean reach in meters (backend estimate)
  geometry: GeoJSON.Polygon;
}

export interface IsochroneResult {
  // Backend returns center as { lng, lat } which matches Coordinates.
  center: Coordinates;
  contours: IsochroneContour[];
}

// Map matching (snap a noisy GPS trace to the road network) result. Normalized
// from OSRM's /match response (matchings[] + tracepoints[]) into the shape the
// Map Matching tool consumes.
export interface MatchResult {
  // Combined snapped-to-road geometry across all matchings, ready to draw.
  matchedGeometry: GeoJSON.LineString;
  // Overall match confidence in [0,1] (distance-weighted mean over matchings).
  confidence: number;
  // How many of the input trace points OSRM could snap (non-null tracepoints).
  snappedPoints: number;
  // How many raw points were submitted.
  totalPoints: number;
  // Snapped route length in meters.
  distance: number;
  // Snapped route travel time in seconds.
  duration: number;
  // Per-input-point snapped location, or null where OSRM dropped the point.
  snappedLocations: (Coordinates | null)[];
}

const asFiniteNumber = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

// Reads a [lng, lat] pair from OSRM (e.g. a tracepoint's `location`) into
// Coordinates, returning null when it is missing/malformed.
const readLngLat = (value: unknown): Coordinates | null => {
  if (!Array.isArray(value) || value.length < 2) return null;
  const lng = value[0];
  const lat = value[1];
  if (typeof lng !== 'number' || typeof lat !== 'number') return null;
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return { lng, lat };
};

// Normalizes OSRM's raw /match response into a MatchResult. Exported for unit
// testing. `totalPoints` is the count of raw points submitted so the result can
// report snapped/total even when OSRM returns no usable matchings.
export const normalizeMatchResponse = (payload: unknown, totalPoints: number): MatchResult => {
  const empty: MatchResult = {
    matchedGeometry: { type: 'LineString', coordinates: [] },
    confidence: 0,
    snappedPoints: 0,
    totalPoints,
    distance: 0,
    duration: 0,
    snappedLocations: new Array(totalPoints).fill(null),
  };

  if (!isRecord(payload)) return empty;

  const matchings = Array.isArray(payload.matchings) ? payload.matchings : [];
  const coordinates: [number, number][] = [];
  let distance = 0;
  let duration = 0;
  let weightedConfidence = 0;
  let confidenceSum = 0;
  let matchingCount = 0;

  for (const m of matchings) {
    if (!isRecord(m)) continue;
    matchingCount += 1;

    const d = asFiniteNumber(m.distance);
    const dur = asFiniteNumber(m.duration);
    const conf = clamp01(asFiniteNumber(m.confidence));
    distance += d;
    duration += dur;
    weightedConfidence += conf * d;
    confidenceSum += conf;

    if (isRecord(m.geometry) && Array.isArray(m.geometry.coordinates)) {
      for (const c of m.geometry.coordinates) {
        const pt = readLngLat(c);
        if (pt) coordinates.push([pt.lng, pt.lat]);
      }
    }
  }

  // Distance-weighted mean is the fairest single confidence when a trace is
  // split into several matchings; fall back to a plain mean when total distance
  // is zero (degenerate/short traces).
  const confidence =
    distance > 0
      ? clamp01(weightedConfidence / distance)
      : matchingCount > 0
        ? clamp01(confidenceSum / matchingCount)
        : 0;

  // One tracepoint per input point: null where OSRM could not snap it.
  const rawTracepoints = Array.isArray(payload.tracepoints) ? payload.tracepoints : [];
  const snappedLocations: (Coordinates | null)[] = new Array(totalPoints).fill(null);
  let snappedPoints = 0;
  for (let i = 0; i < totalPoints; i++) {
    const tp = rawTracepoints[i];
    if (isRecord(tp)) {
      const loc = readLngLat(tp.location);
      if (loc) {
        snappedLocations[i] = loc;
        snappedPoints += 1;
      }
    }
  }

  return {
    matchedGeometry: { type: 'LineString', coordinates },
    confidence,
    snappedPoints,
    totalPoints,
    distance,
    duration,
    snappedLocations,
  };
};

export const routingService = {
  async calculateRoute(request: RouteRequest): Promise<Route[]> {
    // Handle transit routes separately
    if (request.profile === 'transit') {
      return this.calculateTransitRoute(request);
    }

    const waypoints = [
      request.origin,
      ...(request.waypoints || []),
      request.destination,
    ];

    const body = {
      coordinates: waypoints.map((wp) => [wp.lng, wp.lat]),
      profile: request.profile,
      alternatives: request.alternatives ?? true,
      steps: true,
      geometries: 'geojson',
      overview: 'full',
      annotations: true,
      continue_straight: true,
      // Send the individual avoid flags (the backend combines them via
      // buildExclude). The previous `...(cond && { exclude })` spreads all wrote
      // the same `exclude` key, so only the LAST avoid survived — e.g. tolls +
      // highways collapsed to motorway-only.
      avoid_tolls: !!request.avoidTolls,
      avoid_highways: !!request.avoidHighways,
      avoid_ferries: !!request.avoidFerries,
    };

    // Errors propagate so the caller can show a "could not calculate route"
    // message — we never return a fabricated route.
    const response = await api.post<unknown>('/route', body);
    return extractRoutes(response.data);
  },

  async calculateTransitRoute(request: RouteRequest): Promise<Route[]> {
    const body = {
      origin: { lat: request.origin.lat, lng: request.origin.lng },
      destination: { lat: request.destination.lat, lng: request.destination.lng },
      departure_time: request.departureTime?.toISOString() || new Date().toISOString(),
      alternatives: request.alternatives ?? true,
    };

    const response = await api.post<unknown>('/route/transit', body);
    return extractRoutes(response.data);
  },

  async getDistanceMatrix(
    origins: Coordinates[],
    destinations: Coordinates[],
    profile: RouteProfile = 'driving'
  ): Promise<DistanceMatrixResult> {
    const body = {
      origins: origins.map((o) => [o.lng, o.lat]),
      destinations: destinations.map((d) => [d.lng, d.lat]),
      profile,
    };

    const response = await api.post<DistanceMatrixResult>(
      '/matrix',
      body
    );
    return response.data;
  },

  async getIsochrone(
    center: Coordinates,
    times: number[], // minutes
    profile: RouteProfile = 'driving'
  ): Promise<IsochroneResult> {
    const params = new URLSearchParams();
    params.append('lat', center.lat.toString());
    params.append('lon', center.lng.toString());
    params.append('times', times.join(','));
    params.append('profile', profile);

    const response = await api.get<IsochroneResult>(
      `/isochrone?${params.toString()}`
    );
    return response.data;
  },

  async optimizeWaypoints(
    waypoints: Coordinates[],
    profile: RouteProfile = 'driving',
    roundTrip = false
  ): Promise<{ optimizedOrder: number[]; route: Route }> {
    const body = {
      coordinates: waypoints.map((wp) => [wp.lng, wp.lat]),
      profile,
      roundtrip: roundTrip,
      source: 'first',
      destination: 'last',
    };

    const response = await api.post<{ optimizedOrder: number[]; route: Route }>(
      '/optimize',
      body
    );
    return response.data;
  },

  async snapToRoad(
    points: Coordinates[],
    profile: RouteProfile = 'driving'
  ): Promise<Coordinates[]> {
    const body = {
      coordinates: points.map((p) => [p.lng, p.lat]),
      profile,
      radiuses: points.map(() => 25), // 25m snap radius
    };

    const response = await api.post<{ matchings: { geometry: { coordinates: number[][] } }[] }>(
      '/match',
      body
    );

    const snapped = response.data.matchings[0]?.geometry.coordinates || [];
    return snapped.map(([lng, lat]) => ({ lng, lat }));
  },

  // Map matching: snap a noisy GPS trace (>= 2 points) to the road network via
  // OSRM /match, returning a normalized MatchResult (snapped geometry, overall
  // confidence, snapped-point count, distance/duration). Errors propagate so the
  // caller can show a failure message — we never fabricate a matched route.
  async matchTrace(
    points: Coordinates[],
    profile: RouteProfile = 'driving',
    radiusMeters = 25
  ): Promise<MatchResult> {
    const body = {
      coordinates: points.map((p) => [p.lng, p.lat]),
      profile,
      // Per-point search radius (meters). A finite, per-point radius keeps the
      // backend's `radiuses=` query well-formed and bounds how far OSRM may snap.
      radiuses: points.map(() => radiusMeters),
    };

    const response = await api.post<unknown>('/match', body);
    return normalizeMatchResponse(response.data, points.length);
  },

  formatDistance(meters: number, unitSystem: 'metric' | 'imperial' = 'metric'): string {
    if (unitSystem === 'imperial') {
      const miles = meters / 1609.344;
      if (miles < 0.1) {
        const feet = meters * 3.28084;
        return `${Math.round(feet)} ft`;
      }
      return `${miles.toFixed(1)} mi`;
    }

    if (meters < 1000) {
      return `${Math.round(meters)} m`;
    }
    return `${(meters / 1000).toFixed(1)} km`;
  },

  formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.round((seconds % 3600) / 60);

    if (hours === 0) {
      return `${minutes} min`;
    }
    if (minutes === 0) {
      return `${hours} hr`;
    }
    return `${hours} hr ${minutes} min`;
  },

  formatArrivalTime(seconds: number): string {
    const arrival = new Date(Date.now() + seconds * 1000);
    return arrival.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  },

  // Enhanced routing methods
  async getRouteTypes(): Promise<{ route_types: RouteTypeInfo[]; preferences: RoutePreference[] }> {
    try {
      const response = await api.get<{ route_types: RouteTypeInfo[]; preferences: RoutePreference[] }>(
        '/route/types'
      );
      return response.data;
    } catch {
      // Return default types if API unavailable
      return {
        route_types: [
          { id: 'fastest', name: 'Fastest', description: 'Quickest route based on current traffic', icon: 'speed' },
          { id: 'shortest', name: 'Shortest', description: 'Shortest distance route', icon: 'straighten' },
          { id: 'scenic', name: 'Scenic', description: 'Route through scenic areas', icon: 'landscape' },
          { id: 'eco', name: 'Eco-Friendly', description: 'Most fuel-efficient route', icon: 'eco' },
        ],
        preferences: [
          { id: 'avoid_tolls', name: 'Avoid Tolls', type: 'boolean' },
          { id: 'avoid_highways', name: 'Avoid Highways', type: 'boolean' },
          { id: 'avoid_ferries', name: 'Avoid Ferries', type: 'boolean' },
          { id: 'avoid_unpaved', name: 'Avoid Unpaved Roads', type: 'boolean' },
        ],
      };
    }
  },

  async calculateAlternativeRoutes(request: EnhancedRouteRequest): Promise<EnhancedRoute[]> {
    const waypoints = [
      request.origin,
      ...(request.waypoints || []),
      request.destination,
    ];

    const body = {
      coordinates: waypoints.map((wp) => [wp.lng, wp.lat]),
      profile: request.profile,
      route_type: request.routeType,
      avoid_tolls: request.avoidTolls,
      avoid_highways: request.avoidHighways,
      avoid_ferries: request.avoidFerries,
      avoid_unpaved: request.avoidUnpaved,
      departure_time: request.departureTime,
      traffic_model: request.trafficModel,
      steps: true,
      geometries: 'geojson',
      overview: 'full',
    };

    const response = await api.post<unknown>(
      '/route/alternatives',
      body
    );
    return extractRoutes(response.data) as EnhancedRoute[];
  },

  async calculateWaypointsRoute(
    waypoints: Coordinates[],
    options: {
      profile?: RouteProfile;
      routeType?: RouteType;
      optimize?: boolean;
      roundTrip?: boolean;
      avoidTolls?: boolean;
      avoidHighways?: boolean;
      avoidFerries?: boolean;
    } = {}
  ): Promise<{ routes: EnhancedRoute[]; waypoints: Coordinates[]; optimized: boolean; optimizedOrder: number[] }> {
    const body = {
      waypoints: waypoints.map((wp) => ({ lng: wp.lng, lat: wp.lat })),
      profile: options.profile || 'driving',
      route_type: options.routeType || 'fastest',
      optimize: options.optimize || false,
      roundtrip: options.roundTrip || false,
      avoid_tolls: options.avoidTolls || false,
      avoid_highways: options.avoidHighways || false,
      avoid_ferries: options.avoidFerries || false,
    };

    const response = await api.post<unknown>(
      '/route/waypoints',
      body
    );
    return extractWaypointResponse(response.data);
  },
};
